import React, { useRef } from 'react';
import '../styles/ads-alignment.css';
import '../styles/scope-choice.css';
import '../styles/ads.css';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import PageMeta from '../components/PageMeta';
import Reveal from '../components/Reveal';
import ArceusMascot from '../components/arceus3d';
import CustomBuilds from '../components/CustomBuilds';
import DepartmentsStrip from '../components/DepartmentsStrip';
import Capabilities from '../components/Capabilities';
import AppMarquee from '../components/AppMarquee';
import IndustryIcon from '../components/IndustryIcon';
import LeadTicker from '../components/LeadTicker';
import OfferGrid from '../components/OfferGrid';
import Reel, { ReelArrows, ReelHandle } from '../components/Reel';
import keanuPortrait from '../assets/optimized/ai-headshot-keanu.webp';
import justinPortrait from '../assets/optimized/ai-headshot-justin.webp';
import {
  ABOUT_SHORT,
  ARCEUS_URL,
  ARCEUS_ABOUT,
  CITIES,
  CONNECTED_TODAY,
  CONNECT_COMING_SOON,
  CONNECT_ON_REQUEST,
  EMAIL,
  INDUSTRIES_LEDE,
  INDUSTRY_CARDS,
  PHONE_DISPLAY,
  PHONE_HREF,
  POSITIONING,
  UGC_URL,
} from '../data/site';
import { HOME_SCENES_REEL, HOME_SOFTWARE_REEL } from '../data/ads';
import { track, EVENTS } from '../utils/analytics';

const META_TITLE = 'JK Automations | More customers for local businesses';
const META_DESCRIPTION =
  'JK Automations can run your marketing end to end or help with one part: ads, lead handling, custom software and automation built around your team in Los Angeles and San Diego.';

const STRUCTURED_DATA = {
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: 'JK Automations',
  description: POSITIONING,
  url: 'https://jk-automations.com/',
  email: 'hello@jk-automations.com',
  telephone: '+1-213-297-7730',
  sameAs: ['https://www.instagram.com/jk_automations/'],
  areaServed: ['Los Angeles, California', 'San Diego, California'],
  knowsAbout: [
    'Paid social advertising',
    'Google Ads',
    'Ad creative and video production',
    'Call answering and lead tracking',
    'Custom software',
  ],
};

const Home: React.FC = () => {
  const reelRef = useRef<ReelHandle>(null);
  const scenesRef = useRef<ReelHandle>(null);

  return (
    <>
      <PageMeta
        title={META_TITLE}
        description={META_DESCRIPTION}
        path="/"
        structuredData={STRUCTURED_DATA}
      />

      <Hero />
      <LeadTicker />

      <section className="band scope-choice" aria-labelledby="scope-choice-title">
        <div className="shell">
          <Reveal className="scope-choice__intro">
            <span className="scope-choice__eyebrow">Built around your business</span>
            <h2 className="h2" id="scope-choice-title">Get the whole team, or just the missing piece.</h2>
            <p>We start with the problem and what you already have. Then we scope only the work that helps.</p>
          </Reveal>
          <div className="scope-choice__grid">
            <Reveal className="scope-choice__card scope-choice__card--run" index={1}>
              <span className="scope-choice__number">01 <span>Run it for me</span></span>
              <h3>We run your marketing.</h3>
              <p>No marketing team? We can handle creative, campaigns, lead tracking and a report you can actually use.</p>
              <span className="scope-choice__example">“Which ads booked jobs last week?”</span>
            </Reveal>
            <Reveal className="scope-choice__card scope-choice__card--team" index={2}>
              <span className="scope-choice__number">02 <span>Work with my team</span></span>
              <h3>We fix the part that's stuck.</h3>
              <p>Have a team already? Bring us in for ad creative, missed-lead follow-up, or software built around your workflow.</p>
              <span className="scope-choice__example">“Which estimates never got a follow-up?”</span>
            </Reveal>
          </div>
          <Reveal className="scope-choice__method" index={3}>
            <strong>Where AI fits</strong>
            <span>We choose people, software, automation or AI based on the job. The tool follows the business problem.</span>
          </Reveal>
        </div>
      </section>

      {/* ---- How we run it ---- */}
      <section id="arceus" className="band band--arceus">
        <div className="shell meet meet--small">
          <Reveal className="meet__figure meet__figure--small">
            <ArceusMascot
              size="md"
              label="Arceus: JK's system and mascot, a robed figure with a gold halo and a three-gem medallion. It looks toward your pointer."
            />
          </Reveal>
          <Reveal className="meet__copy" index={1}>
            <h2 className="h2">{ARCEUS_ABOUT.lead}</h2>
            {ARCEUS_ABOUT.lines.map((line) => (
              <p key={line}>{line}</p>
            ))}
            <a className="link" href={ARCEUS_URL} target="_blank" rel="noopener noreferrer">
              Open Arceus
              <span className="link__arrow" aria-hidden="true">
                ↗
              </span>
            </a>
            <ul className="controls controls--small controls--flow">
              {ARCEUS_ABOUT.controls.map((c) => (
                <li className={`control control--${c.id}`} key={c.id}>
                  <i aria-hidden="true">{c.symbol}</i>
                  <b>{c.name}</b>
                  <span>{c.means}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ---- What you get ---- */}
      <section id="offer" className="band">
        <div className="shell">
          <Reveal className="lede-row">
            <h2 className="h2">What you get.</h2>
            <p>Choose the help you need. We can run the whole marketing loop or strengthen the part your team already owns.</p>
          </Reveal>

          <Reveal index={1}>
            <OfferGrid />
          </Reveal>
        </div>
      </section>

      {/* ---- The ads ---- */}
      <section id="ads" className="band band--tint">
        <div className="shell">
          <Reveal className="lede-row lede-row--reel">
            <div>
              <h2 className="h2">Software ads, made in house.</h2>
              <p>
                Product spots for Arceus and our own tools: screens, type and motion, nothing stock.
                We make the creative, then measure which ads bring inquiries and booked work.
              </p>
            </div>
            <ReelArrows target={reelRef} />
          </Reveal>

          <Reveal index={1}>
            <Reel cards={HOME_SOFTWARE_REEL} ref={reelRef} />
          </Reveal>

          <Reveal className="scenes" index={2}>
            <div className="lede-row lede-row--reel">
              <div>
                <h3 className="h2">Scenes we generate on our own hardware.</h3>
                <p>
                  Fight and driving studies from our video pipeline, rendered in house. The kind of
                  footage a gym, a wrap shop or a dealer could never afford to film.
                </p>
              </div>
              <ReelArrows target={scenesRef} />
            </div>
            <Reel cards={HOME_SCENES_REEL} ref={scenesRef} />
          </Reveal>

          <Reveal className="products__more" index={3}>
            <Link className="link" to="/marketing#software">
              See the library
              <span className="link__arrow" aria-hidden="true">
                →
              </span>
            </Link>
            <a className="link" href={UGC_URL} target="_blank" rel="noopener noreferrer">
              Creator content on the UGC site
              <span className="link__arrow" aria-hidden="true">
                ↗
              </span>
            </a>
          </Reveal>
        </div>
      </section>

      {/* ---- Where your customers come from ---- */}
      <section id="connections" className="band band--tint">
        <div className="shell">
          <Reveal className="wall">
            <div className="wall__head">
              <h2 className="h2">We plug into what you already have.</h2>
              <p className="wall__sub">
                {CONNECTED_TODAY.length} connected today, read-only to start. More are coming soon or built on request.
              </p>
            </div>
            <AppMarquee
              connectedToday={CONNECTED_TODAY.map((a) => ({ ...a, dashed: false }))}
              connectOnRequest={[...CONNECT_COMING_SOON, ...CONNECT_ON_REQUEST].map((a) => ({ ...a, dashed: true }))}
            />
            <p className="wall__legend">
              Solid: connected today. Dashed: coming soon or built on request. Instagram, Google Ads, Analytics and Calendar are read-only.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---- Industries ---- */}
      <section id="industries" className="band">
        <div className="shell">
          <Reveal className="lede-row">
            <h2 className="h2">{INDUSTRIES_LEDE}</h2>
            <p>An HVAC shop may need estimate follow-up. A restaurant may need better creative. A retailer may need inventory software. Start with the problem that matters to yours.</p>
          </Reveal>
          <div className="inds" role="region" aria-label="Industries we work with" tabIndex={0}>
            {INDUSTRY_CARDS.map((c) => (
              <div className="ind" key={c.name}>
                <IndustryIcon name={c.icon} />
                <h3 className="ind__name">{c.name}</h3>
                <p className="ind__ask">“{c.ask}”</p>
              </div>
            ))}
            <div className="ind ind--more">
              <IndustryIcon name="more" />
              <h3 className="ind__name">Yours</h3>
              <p className="ind__ask">What part of your work is costing you the most time or missed business?</p>
            </div>
          </div>
        </div>
      </section>

      <DepartmentsStrip />

      <Capabilities />

      <CustomBuilds />

      {/* ---- About + contact ---- */}
      <section className="close" id="contact">
        <div className="shell close__grid">
          <Reveal className="close__portraits">
            <figure>
              <Link to="/team/keanu">
                <img src={keanuPortrait} alt="Keanu Santos-Frost" loading="lazy" />
              </Link>
              <figcaption>
                <b>Keanu Santos-Frost</b>
                <span>Co-founder &amp; CEO</span>
              </figcaption>
            </figure>
            <figure>
              <Link to="/team/justin">
                <img src={justinPortrait} alt="Justin Gamm" loading="lazy" />
              </Link>
              <figcaption>
                <b>Justin Gamm</b>
                <span>Co-founder &amp; CTO</span>
              </figcaption>
            </figure>
          </Reveal>

          <Reveal className="close__copy" index={1}>
            <h2 className="h2">Two founders. You talk to us.</h2>
            {ABOUT_SHORT.map((paragraph) => (
              <p key={paragraph.slice(0, 20)}>{paragraph}</p>
            ))}

            <div className="close__actions">
              <a
                className="button button--primary"
                href={`mailto:${EMAIL}`}
                onClick={() => track(EVENTS.emailClicked, { location: 'home-close' })}
              >
                {EMAIL}
              </a>
              <a
                className="button button--quiet"
                href={`tel:${PHONE_HREF}`}
                onClick={() => track(EVENTS.phoneClicked, { location: 'home-close' })}
              >
                {PHONE_DISPLAY}
              </a>
            </div>
            <p className="close__where">{CITIES}</p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default React.memo(Home);
